import React, { useEffect, useState, useCallback } from "react";
import { Calendar, momentLocalizer } from "react-big-calendar";
import moment from "moment";
import axios from "axios";
import "react-big-calendar/lib/css/react-big-calendar.css";
import "./calender.css";
import AttendanceModal from "./AttendanceModal";
import RightWrapper from "../../Components/CommonComponents/ComponentWrapper";
import { Button } from "../../Components/UI";
import { FaCalendarCheck, FaSync } from "react-icons/fa";
import { END_POINT, getJwtToken } from "../../Redux/AdminReducer/action";

const localizer = momentLocalizer(moment);

const AttendanceCalendar = ({ embedded = false }) => {
  const [events, setEvents] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const fetchCalendar = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await axios.get(`${END_POINT}/calendar`, {
        headers: {
          Authorization: getJwtToken(),
        },
      });

      const mapped = (res.data || []).map((item) => ({
        id: item._id,
        title: (item.title || "").toUpperCase(),
        start: new Date(item.start),
        end: new Date(item.end),
        allDay: true,
        description: item.description,
      }));

      setEvents(mapped);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to load calendar data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCalendar();
  }, [fetchCalendar]);

  const getEventStyle = (event) => {
    const title = (event.title || "").toUpperCase();

    if (title.includes("SUNDAY")) {
      return {
        className: "transition-all hover:scale-[1.01]",
        style: {
          background: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)", // Amber
          borderRadius: "8px",
          border: "none",
          color: "#ffffff",
          fontWeight: "600",
          boxShadow: "0 1px 3px rgba(217, 119, 6, 0.25)",
        },
      };
    }

    if (title.includes("HOLIDAY")) {
      return {
        className: "transition-all hover:scale-[1.01]",
        style: {
          background: "linear-gradient(135deg, #f43f5e 0%, #e11d48 100%)", // Rose
          borderRadius: "8px",
          border: "none",
          color: "#ffffff",
          fontWeight: "600",
          boxShadow: "0 1px 3px rgba(225, 29, 72, 0.25)",
        },
      };
    }

    return {
      className: "transition-all hover:scale-[1.01]",
      style: {
        background: "linear-gradient(135deg, #10b981 0%, #059669 100%)", // Emerald
        borderRadius: "8px",
        border: "none",
        color: "#ffffff",
        fontWeight: "600",
        boxShadow: "0 1px 3px rgba(5, 150, 105, 0.25)",
      },
    };
  };

  const content = (
      <div className={embedded ? "" : "p-4 md:p-6 bg-slate-50 min-h-screen"}>
        {/* Top Header Card */}
        <div className="bg-white rounded-2xl shadow-xs border border-slate-200/80 p-4 md:p-6 mb-6">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2.5">
                <div className="w-10 h-10 rounded-xl bg-sky-100 text-sky-600 flex items-center justify-center text-lg">
                  <FaCalendarCheck />
                </div>
                Mark Attendance
              </h1>
              <p className="text-sm text-slate-500 mt-1">
                Click any date cell or event to view or log attendance for doctors, receptionists, consultants and staff.
              </p>
            </div>

            {/* Right Tools: Status Legend & Refresh */}
            <div className="flex flex-wrap items-center gap-3">
              {/* Legend Badges */}
              <div className="flex items-center gap-2 bg-slate-50 px-3 py-1.5 rounded-xl border border-slate-200 text-xs font-semibold">
                <span className="flex items-center gap-1.5 text-amber-700">
                  <span className="w-2.5 h-2.5 rounded-full bg-amber-500"></span>
                  Sunday
                </span>
                <span className="text-slate-300">|</span>
                <span className="flex items-center gap-1.5 text-rose-700">
                  <span className="w-2.5 h-2.5 rounded-full bg-rose-500"></span>
                  Holiday
                </span>
                <span className="text-slate-300">|</span>
                <span className="flex items-center gap-1.5 text-emerald-700">
                  <span className="w-2.5 h-2.5 rounded-full bg-emerald-500"></span>
                  Workday
                </span>
              </div>

              <Button
                variant="outline"
                size="sm"
                onClick={fetchCalendar}
                disabled={loading}
                icon={<FaSync className={loading ? "animate-spin" : ""} />}
              >
                Refresh
              </Button>
            </div>
          </div>

          {error && (
            <div className="mt-4 p-3 rounded-xl bg-rose-50 border border-rose-200 text-rose-700 text-sm flex items-center justify-between">
              <span>{error}</span>
              <button
                type="button"
                onClick={() => setError("")}
                className="text-rose-500 hover:text-rose-700 font-bold ml-2"
              >
                &times;
              </button>
            </div>
          )}
        </div>

        {/* Calendar Card */}
        <div className="bg-white rounded-2xl shadow-xs border border-slate-200/80 p-4 md:p-6">
          <div className="w-full h-[650px] md:h-[750px]">
            <Calendar
              localizer={localizer}
              events={events}
              startAccessor="start"
              endAccessor="end"
              selectable
              style={{ height: "100%", width: "100%" }}
              onSelectEvent={(event) => setSelectedDate(event.start)}
              onSelectSlot={(slot) => setSelectedDate(slot.start)}
              eventPropGetter={getEventStyle}
            />
          </div>
        </div>

        {/* Modal for marking / submitting attendance */}
        {selectedDate && (
          <AttendanceModal
            date={selectedDate}
            onClose={() => {
              setSelectedDate(null);
              fetchCalendar();
            }}
          />
        )}
      </div>
  );

  return embedded ? content : <RightWrapper>{content}</RightWrapper>;
};

export default AttendanceCalendar;
